import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import PropTypes from "prop-types";
import { FixedSizeList } from "react-window";

import DeleteModal from "../../UI/Modal/DeleteModal";
import GenericModalWizard from "../../UI/Modal/GenericModalWizard";
import ChangeOwnerModalWizard from "../../UI/Modal/ChangeOwnerModalWizard";
import Button from "../../UI/Button/Button";
import CheckBoxButton, { CheckBoxLabelSize } from "../../UI/Button/CheckboxButton";
import SelectMultiColumns from "../../UI/Select/SelectMultiColumns";
import Deployment from "./Application/Deployment";
import Admin from "./Application/Admin";
import ApplicationSearchBar from "./ApplicationSearchBar";
import EmptyState from "../../UI/State/EmptyState";
import { ExportToCsv } from "export-to-csv";

import * as ApplicationsActions from "../../../middleware/Store/Actions/ApplicationsActions";
import * as ContactUtils from "../../Utils/ContactUtils";
import * as ApplicationsUtils from "../../Utils/ApplicationsUtils";
import * as ErrorUtils from "../../Utils/ErrorUtils";
import * as ApplicationConstant from "../../Constants/ApplicationConstant";

import { faTrashAlt, faChartLine, faPencilAlt, faSort, faRocket, faBox, faStore, faCog, faExclamation, faExclamationTriangle, faSortAlphaDown, faSortAlphaUp, faThList, faDownload, faSyncAlt, faCheckCircle, faTimesCircle, faUnlock, faLock, faStopCircle, faPlayCircle, faExchangeAlt } from "@fortawesome/free-solid-svg-icons";
import { faCreditCard, faBuilding } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import styles from "./Applications.module.scss";
import LogService from "../../../middleware/CommonSdk/LogService";
import $ from "jquery";

const LOG_TAG = "APPLICATION_LIST";

const ApplicationsList = (props) => {
    const intl = useIntl();
    const history = useHistory();
    const [itemsSelected, setItemsSelected] = useState([]);
    const [modal, setModal] = useState(null);
    const [applications, setApplications] = useState([]);
    const [search, setSearch] = useState("");
    const defaultFilterValue = props.isSummary ? ApplicationsUtils.applicationsFilters().My.value : ApplicationsUtils.applicationsFilters().All.value;
    const [filter, setFilter] = useState(defaultFilterValue);
    const [sortOptions, setSortOptions] = useState({
        dataFieldName: ApplicationConstant.dataFieldName.name,
        order: "asc"
    });
    const [visibleColumns, setVisibleColumns] = useState({
        [ApplicationConstant.dataFieldName.id]: false,
        [ApplicationConstant.dataFieldName.name]: true,
        [ApplicationConstant.dataFieldName.ownerName]: !props.isSummary && (props.isSuperAdminOrAppSuperAdmin || !ApplicationsUtils.areOnlyMyApplications(props.user.id, props.applications)),
        [ApplicationConstant.dataFieldName.ownerId]: false,
        [ApplicationConstant.dataFieldName.ownerCompany]: false,
        [ApplicationConstant.dataFieldName.kpi]: !props.isSummary,
        [ApplicationConstant.dataFieldName.envOrState]: true,
        [ApplicationConstant.dataFieldName.dateOfCreation]: !props.isSummary && props.isSuperAdminOrAppSuperAdmin,
        [ApplicationConstant.dataFieldName.actions]: !props.isSummary,
    });

    // Dropdown menu keep open when click inside
    $(document).on('click', '.keep-open .dropdown-menu', function (e) {
        e.stopPropagation();
    });

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect applications", applications.length);

        let filteredApplications = [...props.applications];
        filteredApplications = ApplicationsUtils.searchApplicationByName(search, filteredApplications);
        if (!ApplicationsUtils.getApplicationById(search, filteredApplications)) {//search application by id
            const foundApp = ApplicationsUtils.getApplicationById(search, props.applications);
            if (foundApp)
                filteredApplications.push(foundApp);
        }
        filteredApplications = ApplicationsUtils.filterApplications(filter, filteredApplications, props.user);
        filteredApplications.sort((a, b) => ApplicationsUtils.applicationSortCompareFunction(a, b, sortOptions));

        setApplications(filteredApplications);
        LogService.debug(LOG_TAG, "useEffect applications end");
    }, [props.applications, search, filter, sortOptions]);

    const noDataFormatter = () => {
        let display =
            <EmptyState
                icon={faRocket}
                text={intl.messages.applicationsMessageNoApplicationFound}
            />;
        return display;
    }

    const stateFormatter = (cell, row) => {
        const { label, className } = ApplicationsUtils.getApplicationStateAndStyle(row);
        return <span className={"badge-dashed small " + className}>{label}</span>
    }

    const csvStateFormatter = (cell, row) => {
        const { label, className } = ApplicationsUtils.getApplicationStateAndStyle(row);
        return label;
    }

    const kpiFormatter = (cellValue) => {
        let iconComponent = null;
        switch (cellValue) {
            case ApplicationConstant.ModelType.Business:
                iconComponent = <span data-toggle="tooltip" title={ApplicationConstant.ModelTypeLabel[ApplicationConstant.ModelType.Business]}><FontAwesomeIcon icon={faBuilding} /></span>;
                break;
            case ApplicationConstant.ModelType.Internal:
                iconComponent = <span data-toggle="tooltip" title={ApplicationConstant.ModelTypeLabel[ApplicationConstant.ModelType.Internal]}><FontAwesomeIcon icon={faBox} /></span>;
                break;
            case ApplicationConstant.ModelType.Demo:
                iconComponent = <span data-toggle="tooltip" title={ApplicationConstant.ModelTypeLabel[ApplicationConstant.ModelType.Demo]}><FontAwesomeIcon icon={faCog} /></span>;
                break;
            case ApplicationConstant.ModelType.RainbowStore:
                iconComponent = <span data-toggle="tooltip" title={ApplicationConstant.ModelTypeLabel[ApplicationConstant.ModelType.RainbowStore]}><FontAwesomeIcon icon={faStore} /></span>;
                break;
            default:
                iconComponent = <span data-toggle="tooltip" title={ApplicationConstant.ModelTypeLabel[ApplicationConstant.ModelType.PayAsYouGo]}><FontAwesomeIcon icon={faCreditCard} /></span>;
                break;
        }
        return iconComponent;
    }

    const caretFormatter = (order) => {
        if (!order) return (<span><FontAwesomeIcon icon={faSort} className="ml-2" /></span>);
        else if (order === "asc") return (<span><FontAwesomeIcon icon={faSortAlphaDown} className="ml-2" /></span>);
        else if (order === "desc") return (<span><FontAwesomeIcon icon={faSortAlphaUp} className="ml-2" /></span>);
        return null;
    }

    const dateFormatter = (date) => {
        return intl.formatDate(date, { year: "numeric", month: "short", day: "numeric" })
    }

    const csvDateFormatter = (date) => {
        return intl.formatDate(date, { year: "numeric", month: "numeric", day: "numeric" })
    }

    const ownerFormatter = (ownerId, app, rowIndex, extraData) => {
        const foundContact = ContactUtils.getContactById(app.ownerId, extraData);
        const displayName = ContactUtils.getDisplayName(foundContact) || app.ownerId;
        let isStillDeveloper = true;
        if (ContactUtils.isDeveloper(props.user) || props.isSuperAdminOrAppSuperAdmin || ContactUtils.isAppSupport(props.user)) {//not available when logged in as support
            isStillDeveloper = ContactUtils.isDeveloper(foundContact);
        }

        return (
            <div className="d-flex justify-content-center">
                <div className="wrap-ellipsis">{displayName}</div>
                {!isStillDeveloper &&
                    <span className="ml-1" data-tooltip data-placement="left" title={intl.messages.applicationIsNoLongerDeveloperMessage}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" size="sm" />
                    </span>
                }
            </div>
        );
    }

    const csvOwnerFormatter = (ownerId, app, rowIndex, extraData) => {
        const foundContact = ContactUtils.getContactById(app.ownerId, extraData);
        return ContactUtils.getDisplayName(foundContact) || app.ownerId;
    }

    const ownerCompanyNameFormatter = (ownerId, app, rowIndex, extraData) => {
        const foundContact = ContactUtils.getContactById(app.ownerId, extraData);
        const companyName = ContactUtils.getCompanyName(foundContact);

        return (
            <div className="d-flex justify-content-center">
                <div className="wrap-ellipsis">{companyName}</div>
            </div>
        );
    }

    const csvOwnerCompanyNameFormatter = (ownerId, app, rowIndex, extraData) => {
        const foundContact = ContactUtils.getContactById(app.ownerId, extraData);
        return ContactUtils.getCompanyName(foundContact);
    }
    const actionFormatter = (cell, app, rowIndex, extraData) => {
        let isMyApplication = ApplicationsUtils.isMyApplication(app, props.user);
        let isDeployed = (app.env === ApplicationConstant.Env.Deployed);
        let disableActions = extraData === true;

        let ownerActions = [];
        if (isMyApplication || props.isSuperAdminOrAppSuperAdmin) {
            ownerActions.push(
                <Button
                    key="ownerActions0"
                    targetName="#deleteSingleApplicationModal"
                    icon={faTrashAlt}
                    className="btn-grid ml-1"
                    onClick={() => handleTableActionDelete(app.id)}
                    tooltipTitle={intl.messages.delete}
                    disabled={disableActions}
                />
            );
        }
        if (isMyApplication) {
            if (isDeployed && (app.state === ApplicationConstant.State.Active || app.state === ApplicationConstant.State.New)) {
                ownerActions.push(
                    <Button
                        key="ownerActions1"
                        icon={faStopCircle}
                        className="btn-grid ml-1"
                        onClick={() => handleTableActionStop(app.id)}
                        tooltipTitle={intl.messages.applicationEditionDeploymentStopButton}
                        disabled={disableActions}
                    />
                );
            } else if (isDeployed && app.state === ApplicationConstant.State.Stopped) {
                ownerActions.push(
                    <Button
                        key="ownerActions2"
                        icon={faPlayCircle}
                        className="btn-grid ml-1"
                        onClick={() => handleTableActionRestart(app.id)}
                        tooltipTitle={intl.messages.applicationEditionDeploymentRestartButton}
                        disabled={disableActions}
                    />
                );
            } else if (app.env === ApplicationConstant.Env.NotDeployed) {
                let isInvoicesFullyFilled = app.kpi !== ApplicationConstant.ModelType.PayAsYouGo || ApplicationsUtils.isInvoiceAccountFullyFilled(props.invoicesAccount);
                ownerActions.push(
                    <Button
                        key="ownerActions3"
                        icon={faRocket}
                        className="btn-grid ml-1"
                        onClick={() => handleTableActionDeployRequest(app.id)}
                        tooltipTitle={isInvoicesFullyFilled ? intl.messages.applicationEditionDeploymentButton : intl.messages.applicationEditionDeploymentInvoicesAddressErrorMessage}
                        disabled={disableActions || !isInvoicesFullyFilled}
                    />
                );
            }
        }
        let adminActions = [];
        if (props.isSuperAdminOrAppSuperAdmin) {
            if (isDeployed && app.state === ApplicationConstant.State.Blocked) {
                adminActions.push(
                    <Button
                        key="adminActions0"
                        icon={faUnlock}
                        className="btn-grid ml-1"
                        onClick={() => handleTableActionUnblock(app.id)}
                        tooltipTitle={intl.messages.applicationEditionAdminUnblockButton}
                        disabled={disableActions}
                    />
                );
            } else if (isDeployed) {
                adminActions.push(
                    <Button
                        key="adminActions1"
                        icon={faLock}
                        className="btn-grid ml-1"
                        onClick={() => handleTableActionBlock(app.id)}
                        tooltipTitle={intl.messages.applicationEditionAdminBlockButton}
                        disabled={disableActions}
                    />
                );
            } else if (app.env === ApplicationConstant.Env.InDeployment) {
                adminActions.push(
                    <Button
                        key="adminActions2"
                        icon={faCheckCircle}
                        className="btn-grid ml-1"
                        onClick={() => handleTableActionDeploy(app.id)}
                        tooltipTitle={intl.messages.applicationEditionAdminDeployButton}
                        disabled={disableActions}
                    />
                );
                adminActions.push(
                    <Button
                        key="adminActions3"
                        icon={faTimesCircle}
                        className="btn-grid ml-1"
                        onClick={() => handleTableActionDecline(app.id)}
                        tooltipTitle={intl.messages.applicationEditionAdminDeclineButton}
                        disabled={disableActions}
                    />
                );
            }
        }

        return (<span>
            <Button //visible/enabled si l'application est deployed
                icon={faChartLine}
                className={(isMyApplication || props.isSuperAdminOrAppSuperAdmin) && isDeployed ? "btn-grid ml-1" : "invisible btn-grid ml-1"}
                onClick={() => handleTableActionEditStatistics(app.id)}
                tooltipTitle={intl.messages.statistics}
                disabled={!isDeployed || disableActions}
            />
            <Button
                icon={faPencilAlt}
                className="btn-grid ml-1"
                onClick={() => handleTableActionEdit(app.id)}
                tooltipTitle={intl.messages.edit}
                disabled={disableActions}
            />
            {ownerActions}
            {adminActions}
        </span>)
    }

    const handleOnSelect = (row, isSelect) => {
        LogService.trace(LOG_TAG, "handleOnSelect");
        if (isSelect)
            setItemsSelected([...itemsSelected, row.id]);
        else
            setItemsSelected(itemsSelected.filter((appId) => (appId !== row.id)));
    }

    const handleOnSelectAll = (isSelect, rows) => {
        LogService.trace(LOG_TAG, "handleOnSelectAll");
        const ids = applications.map(app => app.id);

        if (isSelect)
            setItemsSelected(ids);
        else
            clearItemsSelected();
    }

    const clearItemsSelected = () => {
        LogService.trace(LOG_TAG, "clearItemsSelected");
        setItemsSelected([]);
    }

    const handleTableActionEditStatistics = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionEditStatistics");
        history.push(ApplicationConstant.APPLICATION_PATH + ApplicationConstant.STATISTICS_PATH + "?" + ApplicationConstant.ApplicationTagName + "=" + appId);
    };

    const handleTableActionEdit = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionEdit");
        history.push(ApplicationConstant.APPLICATION_PATH + ApplicationConstant.EDIT_PATH + "?" + ApplicationConstant.ApplicationTagName + "=" + appId);
    };

    const handleTableActionDelete = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionDelete");
        handleGroupActionDelete([appId]);
    };

    const handleTableActionStop = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionStop");

        const title = intl.formatMessage({ id: "applicationGenericAction" }, { action: intl.messages.applicationEditionDeploymentStopButton });
        const getComponent = (updateEnabled, handleOnSuccess, handleOnFailed) => {
            return (
                <Deployment
                    applicationId={appId}
                    showHeader={false}
                    isUpdateEnabled={updateEnabled}
                    onActionSuccess={() => handleOnSuccess(intl.messages.applicationStopSuccessfullMessage)}
                    onActionFailed={() => handleOnFailed(intl.messages.applicationStopFailedMessage)}
                />);
        }
        setModal(
            <GenericModalWizard
                title={title}
                icon={faStopCircle}
                getComponent={getComponent}
            />
        );
    };

    const handleTableActionRestart = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionRestart");

        const title = intl.formatMessage({ id: "applicationGenericAction" }, { action: intl.messages.applicationEditionDeploymentRestartButton });
        const getComponent = (updateEnabled, handleOnSuccess, handleOnFailed) => {
            return (
                <Deployment
                    applicationId={appId}
                    showHeader={false}
                    isUpdateEnabled={updateEnabled}
                    onActionSuccess={() => handleOnSuccess(intl.messages.applicationRestartSuccessfullMessage)}
                    onActionFailed={() => handleOnFailed(intl.messages.applicationRestartFailedMessage)}
                />);
        }
        setModal(
            <GenericModalWizard
                title={title}
                icon={faPlayCircle}
                getComponent={getComponent}
            />
        );
    };

    const handleTableActionDeployRequest = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionDeployRequest");

        const title = intl.messages.applicationEditionDeploymentButton;
        const getComponent = (updateEnabled, handleOnSuccess, handleOnFailed) => {
            return (
                <Deployment
                    applicationId={appId}
                    showHeader={false}
                    isUpdateEnabled={updateEnabled}
                    onActionSuccess={() => handleOnSuccess(intl.messages.applicationDeployRequestSuccessfullMessage)}
                    onActionFailed={() => handleOnFailed(intl.messages.applicationDeployRequestFailedMessage)}
                />);
        }
        setModal(
            <GenericModalWizard
                title={title}
                icon={faRocket}
                getComponent={getComponent}
            />
        );
    };

    const handleTableActionUnblock = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionUnblock");

        const title = intl.formatMessage({ id: "applicationGenericAction" }, { action: intl.messages.applicationEditionAdminUnblockButton });
        const getComponent = (updateEnabled, handleOnSuccess, handleOnFailed) => {
            return (
                <Admin
                    applicationId={appId}
                    showHeader={false}
                    isUpdateEnabled={updateEnabled}
                    onActionSuccess={() => handleOnSuccess(intl.messages.applicationUnblockSuccessfullMessage)}
                    onActionFailed={() => handleOnFailed(intl.messages.applicationUnblockFailedMessage)}
                />);
        }
        setModal(
            <GenericModalWizard
                title={title}
                icon={faUnlock}
                getComponent={getComponent}
            />
        );
    };

    const handleTableActionBlock = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionBlock");

        const title = intl.formatMessage({ id: "applicationGenericAction" }, { action: intl.messages.applicationEditionAdminBlockButton });
        const getComponent = (updateEnabled, handleOnSuccess, handleOnFailed) => {
            return (
                <Admin
                    applicationId={appId}
                    showHeader={false}
                    isUpdateEnabled={updateEnabled}
                    onActionSuccess={() => handleOnSuccess(intl.messages.applicationBlockSuccessfullMessage)}
                    onActionFailed={() => handleOnFailed(intl.messages.applicationBlockFailedMessage)}
                />);
        }
        setModal(
            <GenericModalWizard
                title={title}
                icon={faLock}
                getComponent={getComponent}
            />
        );
    };

    const handleTableActionDeploy = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionDeploy");

        const title = intl.formatMessage({ id: "applicationGenericAction" }, { action: intl.messages.applicationEditionAdminDeployButton });
        const getComponent = (updateEnabled, handleOnSuccess, handleOnFailed) => {
            return (
                <Admin
                    applicationId={appId}
                    showHeader={false}
                    isUpdateEnabled={updateEnabled}
                    onActionSuccess={() => handleOnSuccess(intl.messages.applicationDeploySuccessfullMessage)}
                    onActionFailed={() => handleOnFailed(intl.messages.applicationDeployFailedMessage)}
                />);
        }
        setModal(
            <GenericModalWizard
                title={title}
                icon={faCheckCircle}
                getComponent={getComponent}
            />
        );
    };

    const handleTableActionDecline = (appId) => {
        LogService.info(LOG_TAG, "handleTableActionDecline");

        const title = intl.formatMessage({ id: "applicationGenericAction" }, { action: intl.messages.applicationEditionAdminDeclineButton });
        const getComponent = (updateEnabled, handleOnSuccess, handleOnFailed) => {
            return (
                <Admin
                    applicationId={appId}
                    showHeader={false}
                    isUpdateEnabled={updateEnabled}
                    onActionSuccess={() => handleOnSuccess(intl.messages.applicationDeclineSuccessfullMessage)}
                    onActionFailed={() => handleOnFailed(intl.messages.applicationDeclineFailedMessage)}
                />);
        }
        setModal(
            <GenericModalWizard
                title={title}
                icon={faTimesCircle}
                getComponent={getComponent}
            />
        );
    };

    const handleDeleteApplications = (applicationIdList) => {
        LogService.info(LOG_TAG, "handleDeleteApplication");

        if (applicationIdList && applicationIdList.length > 0) {
            props.deleteApplications(applicationIdList)
                .then(() => {
                    LogService.debug(LOG_TAG, "handleDeleteApplication success");
                    clearItemsSelected();
                });
        }
    };

    const handleApplicationsRefresh = () => {
        LogService.info(LOG_TAG, "handleApplicationsRefresh");
        if (props.onApplicationsRefresh) {
            props.onApplicationsRefresh();
        }
    };

    const handleApplicationsChangeOwner = (applicationIdList, ownerId) => {
        LogService.info(LOG_TAG, "handleApplicationsChangeOwner");

        if (applicationIdList && applicationIdList.length > 0 && ownerId) {
            return props.changeOwnerApplications(applicationIdList, ownerId)
                .then(() => {
                    LogService.debug(LOG_TAG, "handleApplicationsChangeOwner success");
                    clearItemsSelected();
                    return Promise.resolve({
                        title: intl.messages.applicationChangeOwnerSuccessfullMessage,
                    });
                })
                .catch((errorPayloadArray) => {
                    LogService.debug(LOG_TAG, "handleApplicationsChangeOwner failed");
                    clearItemsSelected();
                    const resolvedNameErrorArray = errorPayloadArray.map(item => {
                        let applicationName = null;
                        let errorObject = null;
                        Object.entries(item).map(([key, value]) => {
                            applicationName = ApplicationsUtils.getApplicationById(key, props.applications)?.name;
                            errorObject = value;
                        })
                        return { [applicationName]: errorObject };
                    });
                    const errorMessage = ErrorUtils.parseErrorMessageArray(resolvedNameErrorArray);
                    return Promise.reject({
                        title: intl.messages.applicationChangeOwnerFailedMessage,
                        errorMessage: errorMessage
                    });
                });
        } else {
            return Promise.reject({
                title: intl.messages.applicationChangeOwnerFailedMessage,
                errorMessage: "something went wrong"
            });
        }
    };

    const handleGroupActionDelete = (applicationIdList) => {
        LogService.info(LOG_TAG, "handleGroupActionDelete");

        const description = intl.formatMessage({ id: "applicationActionDeleteMessage" }, { value: applicationIdList.length });
        setModal(
            <DeleteModal
                title={intl.messages.applicationActionDeleteTitle}
                icon={faExclamation}
                btnlabel={intl.messages.delete}
                onClick={() => handleDeleteApplications(applicationIdList)}
                showModalOnInit={true}
            >
                <p>{description}</p>
                <div className="list">{ApplicationsUtils.getApplicationListNames(applicationIdList, props.applications)}</div>
            </DeleteModal>
        );
    };

    const handleGroupActionOwnerChange = (applicationIdList) => {
        LogService.info(LOG_TAG, "handleGroupActionOwnerChange");

        const getComponent = (enableAction, selectedContact, resetValue) => {
            let developerList = ContactUtils.getDevelopers([...props.contacts, props.user]);
            if (ApplicationsUtils.areOnlyInternalApplications(applicationIdList, props.applications))
                developerList = ContactUtils.getInternalDevelopers([...props.contacts, props.user]);
            const selectionList = ContactUtils.sortByCompanyAndName(developerList);

            let ownerSelection = <SelectMultiColumns
                itemList={selectionList}
                firstColumnDisplayFunction={(contact) => ContactUtils.getDisplayName(contact)}
                onItemSelection={(contact) => enableAction(contact)}
                selectedItem={resetValue ? null : selectedContact}
                defaultValue={intl.messages.applicationGroupActionChangeOwnerPlaceholder}
                dropdownClassName="w-100"
            />

            if (props.isSuperAdminOrAppSuperAdmin) {
                ownerSelection = <SelectMultiColumns
                    itemList={selectionList}
                    firstColumnDisplayFunction={(contact) => ContactUtils.getDisplayName(contact)}
                    secondColumnDisplayFunction={(contact) => contact?.companyName}
                    onItemSelection={(contact) => enableAction(contact)}
                    selectedItem={resetValue ? null : selectedContact}
                    defaultValue={intl.messages.applicationGroupActionChangeOwnerPlaceholder}
                    dropdownClassName="w-100"
                />
            }

            const description = intl.formatMessage({ id: "applicationActionChangeOwnerMessage" }, { value: applicationIdList.length });

            return (
                <div className="mt-2 mb-2 mr-3 ml-3">
                    <p className="mt-3 text-center">{description}</p>
                    <div className="list text-center">{ApplicationsUtils.getApplicationListNames(applicationIdList, props.applications)}</div>
                    <label className="input-label">{intl.messages.applicationEditionOwnerChangeOwnerTitle}</label>
                    {ownerSelection}
                </div>
            );
        }
        setModal(
            <ChangeOwnerModalWizard
                title={intl.messages.applicationActionChangeOwnerTitle}
                icon={faExchangeAlt}
                getComponent={getComponent}
                action={(contact) => handleApplicationsChangeOwner(applicationIdList, contact?.id)}
                disableFirstButton={true}
            />
        );
    };

    const handleExportCsv = () => {
        LogService.info(LOG_TAG, "handleExportCsv");

        const options = {
            fieldSeparator: ";",
            quoteStrings: '"',
            decimalSeparator: ".",
            showLabels: true,
            showTitle: false,
            filename: "applications",
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: true,
        };

        let csvApplications = applications.map(app => {
            const filteredValues = {};
            columns.forEach(column => {
                if (column.dataField === ApplicationConstant.dataFieldName.ownerName) {
                    filteredValues[column.text] = csvOwnerFormatter(null, app, null, [...props.contacts, props.user]);
                } else if (column.dataField === ApplicationConstant.dataFieldName.ownerCompany) {
                    filteredValues[column.text] = csvOwnerCompanyNameFormatter(null, app, null, [...props.contacts, props.user]);
                } else if (column.dataField === ApplicationConstant.dataFieldName.envOrState) {
                    filteredValues[column.text] = csvStateFormatter(null, app);
                } else if (column.dataField === ApplicationConstant.dataFieldName.dateOfCreation) {
                    filteredValues[column.text] = csvDateFormatter(app.dateOfCreation);
                } else if (!column.isDummyField) {
                    filteredValues[column.text] = app[column.dataField];
                }
            });
            return filteredValues;
        });

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(csvApplications);
    };

    LogService.trace(LOG_TAG, "render", props.applications.length);

    const displaySearchAndFilter = !props.isSummary && props.applications.length > ApplicationConstant.DisplaySearchAndFilterCount;
    const diplayGroupActions = !props.isSummary && props.applications.length > 0;
    const hideHeaderClassName = props.applications.length > 0 ? "" : " d-none";
    const myApplicationsCount = ApplicationsUtils.myApplications(props.applications, props.user).length;

    const selectRow = {
        mode: "checkbox",
        classes: "bsSelectionRow", //bg color of items if selected
        hideSelectColumn: props.isSummary,
        onSelect: handleOnSelect,
        onSelectAll: handleOnSelectAll
    };

    const columns = [{
        dataField: ApplicationConstant.dataFieldName.id,
        text: "Application id",
        isTogglelable: true,
        align: "center",
        headerAlign: "center",
        hidden: !visibleColumns.id,
        sort: true,
        sortCaret: caretFormatter,
        onSort: (field, order) => {
            setSortOptions({
                dataFieldName: field,
                order: order
            })
        },
        searchable: false
    }, {
        dataField: ApplicationConstant.dataFieldName.name,
        text: intl.messages.applicationName,
        align: "left",
        hidden: !visibleColumns.name,
        sort: true,
        sortCaret: caretFormatter,
        onSort: (field, order) => {
            setSortOptions({
                dataFieldName: field,
                order: order
            })
        },
        searchable: false
    }, {
        dataField: ApplicationConstant.dataFieldName.ownerName,//fake field
        isDummyField: true,
        isTogglelable: true,
        text: intl.messages.applicationOwner,
        align: "center",
        headerAlign: "center",
        hidden: !visibleColumns.ownerName,
        sort: true,
        sortCaret: caretFormatter,
        onSort: (field, order) => {
            setSortOptions({
                dataFieldName: field,
                contacts: [...props.contacts, props.user],
                order: order
            })
        },
        searchable: false,
        formatter: ownerFormatter,
        formatExtraData: [...props.contacts, props.user]
    }, {
        dataField: ApplicationConstant.dataFieldName.ownerId,
        text: intl.messages.applicationOwnerId,
        isTogglelable: true,
        align: "center",
        headerAlign: "center",
        hidden: !visibleColumns.ownerId,
        sort: true,
        sortCaret: caretFormatter,
        onSort: (field, order) => {
            setSortOptions({
                dataFieldName: field,
                order: order
            })
        },
        searchable: false
    }, {
        dataField: ApplicationConstant.dataFieldName.ownerCompany,//fake field
        isTogglelable: true,
        isDummyField: true,
        text: intl.messages.applicationOwnerCompanyName,
        align: "center",
        headerAlign: "center",
        hidden: !visibleColumns.ownerCompany,
        sort: true,
        sortCaret: caretFormatter,
        onSort: (field, order) => {
            setSortOptions({
                dataFieldName: field,
                contacts: [...props.contacts, props.user],
                order: order
            })
        },
        searchable: false,
        formatter: ownerCompanyNameFormatter,
        formatExtraData: [...props.contacts, props.user]
    }, {
        dataField: ApplicationConstant.dataFieldName.kpi,
        text: intl.messages.applicationOffer,
        isTogglelable: true,
        align: "center",
        headerAlign: "center",
        headerStyle: {
            width: "70px"
        },
        hidden: !visibleColumns.kpi,
        sort: true,
        sortCaret: caretFormatter,
        onSort: (field, order) => {
            setSortOptions({
                dataFieldName: field,
                order: order
            })
        },
        searchable: false,
        formatter: kpiFormatter
    }, {
        dataField: ApplicationConstant.dataFieldName.envOrState,//fake field
        isDummyField: true,
        isTogglelable: true,
        text: intl.messages.applicationState,
        align: "center",
        headerAlign: "center",
        headerStyle: {
            width: "150px"
        },
        hidden: !visibleColumns.envOrState,
        sort: true,
        sortCaret: caretFormatter,
        onSort: (field, order) => {
            setSortOptions({
                dataFieldName: field,
                order: order
            })
        },
        searchable: false,
        formatter: stateFormatter
    }, {
        dataField: ApplicationConstant.dataFieldName.dateOfCreation,
        text: intl.messages.applicationCreatedOn,
        isTogglelable: true,
        align: "center",
        headerAlign: "center",
        headerStyle: {
            width: "110px"
        },
        hidden: !visibleColumns.dateOfCreation,
        sort: true,
        sortCaret: caretFormatter,
        onSort: (field, order) => {
            setSortOptions({
                dataFieldName: field,
                order: order
            })
        },
        searchable: false,
        formatter: dateFormatter
    }, {
        dataField: ApplicationConstant.dataFieldName.actions,//fake field
        isDummyField: true,
        text: "Actions",
        align: "left",
        headerAlign: "center",
        headerStyle: {
            width: props.isSuperAdminOrAppSuperAdmin ? "120px" : "100px"
        },
        hidden: !visibleColumns.actions,
        searchable: false,
        formatter: actionFormatter,
        formatExtraData: itemsSelected.length > 1
    }];

    const exportCsv =
        <Button
            id="btnGroupExportCsv"
            icon={faDownload}
            className="btn-gray"
            onClick={handleExportCsv}
            tooltipTitle={intl.messages.applicationGroupActionExportButtonTooltip}
        />;

    const columnToggle =
        <div className="btn-group keep-open" role="group">
            <button
                id="btnGroupColumnToggle"
                className="btn btn-secondary dropdown-toggle"
                type="button"
                data-toggle="dropdown"
                title={intl.messages.applicationGroupActionColumnToggleButtonTooltip}
                data-tooltip
            >
                <FontAwesomeIcon className="mr-1" icon={faThList} />
            </button>
            <ul className="dropdown-menu" aria-labelledby="btnGroupColumnToggle">
                {
                    Object.entries(visibleColumns).map(([key, value]) => {
                        let isTogglelable = true;
                        isTogglelable = columns.find(item => item.dataField === key).isTogglelable;

                        return(isTogglelable && <li key={key} className="dropdown-item pl-2">
                            <CheckBoxButton
                                checked={value}
                                label={key}
                                labelSize={CheckBoxLabelSize.Medium}
                                onClick={() => setVisibleColumns({ ...visibleColumns, [key]: !value })}
                            />
                        </li>)
                    })
                }
            </ul>
        </div>;

    let groupButton = [];
    if (itemsSelected.length > 0) {
        const areMyApplications = ApplicationsUtils.areOnlyMyApplications(props.user.id, props.applications, itemsSelected);
        const enableDelete = props.isSuperAdminOrAppSuperAdmin || areMyApplications;
        groupButton.push(
            <Button
                key="groupButton0"
                id="btnGroupDelete"
                icon={faTrashAlt}
                className="btn-gray"
                onClick={() => handleGroupActionDelete(itemsSelected)}
                tooltipTitle={enableDelete ? intl.messages.applicationGroupActionDeleteButtonTooltip : intl.messages.applicationGroupActionDeleteButtonTooltipDisabled}
                disabled={!enableDelete}
            />
        );

        if (ContactUtils.isDeveloper(props.user) || props.isSuperAdminOrAppSuperAdmin) {
            const enableChangeOwner = ApplicationsUtils.areOnlyBusinessApplications(itemsSelected, props.applications) || ApplicationsUtils.areOnlyInternalApplications(itemsSelected, props.applications);
            groupButton.push(
                <Button
                    key="groupButton1"
                    id="btnGroupChangeOwner"
                    icon={faExchangeAlt}
                    className="btn-gray"
                    onClick={() => handleGroupActionOwnerChange(itemsSelected)}
                    tooltipTitle={enableChangeOwner ? intl.messages.applicationGroupActionChangeOwnerButtonTooltip : intl.messages.applicationGroupActionChangeOwnerButtonTooltipDisabled}
                    disabled={!enableChangeOwner}
                />
            );
        }
    }

    const TableRow = ({ index, style }) => {
        const application = applications[index];
        const row =
            <div id="tableRow" style={style} className={"d-flex border-bottom border-smoke " + styles.tableBody}>
                <span className="selection-cell"
                    style={{
                        display: props.isSummary ? "none" : "d-flex",
                    }}>
                    <input type="checkbox" className="selection-input-4" checked={itemsSelected.indexOf(application.id) >= 0} onChange={event => handleOnSelect(application, event.target.checked)} />
                </span>
                <span id="id"
                    style={{
                        textAlign: columns[0].align,
                        display: columns[0].hidden === true ? "none" : "d-flex",
                        flex: 1
                    }}
                >
                    {application.id}
                </span>
                <span
                    style={{
                        textAlign: columns[1].align,
                        display: columns[1].hidden === true ? "none" : "d-flex",
                        flex: 1
                    }}
                >
                    {application.name}
                </span>
                <span
                    style={{
                        textAlign: columns[2].align,
                        display: columns[2].hidden === true ? "none" : "d-flex",
                        flex: 1
                    }}
                >
                    {ownerFormatter(null, application, null, [...props.contacts, props.user])}
                </span>
                <span
                    style={{
                        textAlign: columns[3].align,
                        display: columns[3].hidden === true ? "none" : "d-flex",
                        flex: 1
                    }}
                >
                    {application.ownerId}
                </span>
                <span
                    style={{
                        textAlign: columns[4].align,
                        display: columns[4].hidden === true ? "none" : "d-flex",
                        flex: 1
                    }}
                >
                    {ownerCompanyNameFormatter(null, application, null, [...props.contacts, props.user])}
                </span>
                <span
                    style={{
                        textAlign: columns[5].align,
                        display: columns[5].hidden === true ? "none" : "d-flex",
                        flexBasis: columns[5].headerStyle.width
                    }}
                >
                    {kpiFormatter(application.kpi)}
                </span>
                <span
                    style={{
                        textAlign: columns[6].align,
                        display: columns[6].hidden === true ? "none" : "d-flex",
                        flexBasis: columns[6].headerStyle.width
                    }}
                >
                    {stateFormatter(null, application)}
                </span>
                <span
                    style={{
                        textAlign: columns[7].align,
                        display: columns[7].hidden === true ? "none" : "d-flex",
                        flexBasis: columns[7].headerStyle.width
                    }}
                >
                    {dateFormatter(application.dateOfCreation)}
                </span>
                <span
                    style={{
                        textAlign: columns[8].align,
                        display: columns[8].hidden === true ? "none" : "d-flex",
                        // flexGrow: 1,
                        flexBasis: columns[8].headerStyle.width,
                    }}
                >
                    {actionFormatter(null, application, null, itemsSelected.length > 1)}
                </span>
            </div>
        return row;
    };

    let applicationsDisplay = noDataFormatter();
    if (applications.length > 0)
        applicationsDisplay =
            <FixedSizeList
                height={props.isSummary & props.isMaxSize ? 490 : 340}
                itemCount={applications.length}
                itemSize={33}
            >
                {TableRow}
            </FixedSizeList>;

    return (
        <div>
            {modal}

            <div className="d-flex align-items-end" role="toolbar" aria-label="Toolbar with button groups">
                {displaySearchAndFilter &&
                    <ApplicationSearchBar
                        onSearch={(value) => setSearch(value)}
                        onFilter={(filterValue) => setFilter(filterValue)}
                        defaultFilter={myApplicationsCount > 0 ? ApplicationsUtils.applicationsFilters().My.value : ApplicationsUtils.applicationsFilters().All.value}
                    />
                }
                {diplayGroupActions &&
                    <div className="btn-group mb-3 ml-auto" role="group" >
                        {columnToggle}
                        {exportCsv}
                        <Button
                            id="btnGroupRefresh"
                            icon={faSyncAlt}
                            className="btn-gray"
                            onClick={handleApplicationsRefresh}
                            tooltipTitle={intl.messages.applicationGroupActionRefreshButtonTooltip}
                        />
                        {groupButton}
                    </div>
                }
            </div>

            <BootstrapTable
                keyField="id"
                id="bstable"
                data={[{ id: "1" }]} //keep one for select all to work
                columns={columns}
                bordered={false}
                bodyClasses="d-none"
                headerClasses={"bsHeader " + hideHeaderClassName}
                classes="mb-0"
                selectRow={selectRow}
                defaultSorted={[{
                    dataField: ApplicationConstant.dataFieldName.name, // if dataField is not match to any column you defined, it will be ignored.
                    order: "asc" // desc or asc
                }]}
            />

            {applicationsDisplay}
        </div >
    );
}

ApplicationsList.defaultProps = {
    isSummary: false,
    isMaxSize: false,
    onApplicationsRefresh: null
}

ApplicationsList.propTypes = {
    isSummary: PropTypes.bool,
    isMaxSize: PropTypes.bool,
    onApplicationsRefresh: PropTypes.func
};

const mapStateToProps = state => {
    return {
        user: state.contacts.user,
        isSuperAdminOrAppSuperAdmin: ContactUtils.isSuperAdminOrAppSuperAdmin(state.contacts.user),
        applications: state.applications.applications,
        contacts: state.contacts.contacts,
        invoicesAccount: state.invoices.account
    };
};

const mapDispatchToProps = dispatch => {
    return {
        deleteApplications: (appIdList) => dispatch(ApplicationsActions.deleteApplications(appIdList)),
        changeOwnerApplications: (appIdList, ownerId) => dispatch(ApplicationsActions.changeOwnerApplications(appIdList, ownerId)),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationsList);
